// src/App.js
import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router, Routes, Route, Navigate, useLocation
} from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Pages existantes
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Events from './pages/Events';
import Announcements from './pages/Announcements';
import Resources from './pages/Resources';
import Courses from './pages/Courses';
import Stats from './pages/Stats';
import UserForm from './pages/UserForm';
import CourseForm from './pages/CourseForm';
import AnnouncementForm from './pages/AnnouncementForm';
import EventForm from './pages/EventForm';
import EventEdit from './pages/EventEdit';
import ResourceForm from './pages/ResourceForm';
import FolderForm from './pages/FolderForm';
import SchoolRules from './pages/SchoolRules';
import PageNotFound from './pages/PageNotFound';
import UserDetails from './pages/UserDetails';
import AnnouncementDetails from './pages/AnnouncementDetails';
import CourseDetails from './pages/CourseDetails';
import CategoryList from './components/CategoryList';
import CategoryForm from './components/CategoryForm';
import CourseEdit from './pages/CourseEdit';
import DanceStyles from './pages/DanceStyles';
import Registrations from './pages/Registrations';
import Subscriptions from './pages/Subscriptions';
import Payments from './pages/Payments';
import Documents from './pages/Documents';
import DocumentDetails from './pages/DocumentDetails';
import Goals from './pages/Goals';
import GoalDetails from './pages/GoalDetails';
import Notifications from './pages/Notifications';
import Accounting from './pages/Accounting';
import AttendancePage from './pages/AttendancePage';
import GoalForm from './pages/GoalForm';
import Teachers from './pages/Teachers';
import TeacherDetails from './pages/TeacherDetails';
import BlogManager from './pages/website/BlogManager';
import ClassManager from './pages/website/ClassManager';
import BlogForm from './pages/website/BlogForm';
import WebsiteManager from './pages/WebsiteManager';
import EventDetails from './pages/EventDetails';
import Categories from './pages/Categories';
import TopPages from './pages/TopPages';
import Contacts from './pages/Contacts';
import Students from './pages/Students';
import Parents from './pages/Parents';
import ParentForm from './pages/ParentForm';
import PaymentForm from './pages/PaymentForm';
import ProductList from './pages/ProductList';
import ProductForm from './pages/ProductForm';
import ProductOrders from './pages/ProductOrders';
import StudentEvolutions from './pages/StudentEvolutions';
import StudentEvolutionDetails from './pages/StudentEvolutionDetails';
import StudentEvolutionForm from './pages/StudentEvolutionForm';
import CrewAuditions from './pages/CrewAuditions';
import ChatListPage from './pages/ChatListPage';   // liste des chats
import ChatPage from './pages/ChatPage';           // vue d'un chat
import ChatInfoPage from './pages/ChatInfoPage';   // infos / participants
import EvaluationForm from './pages/EvaluationForm';
import Planning from './pages/Planning';
import Layout from './pages/Layout';
import TestMolliePayment from './pages/TestMolliePayment';
import ChartOfAccounts from './pages/accounting/ChartOfAccounts';
import JournalEntries from './pages/accounting/JournalEntries';
import JournalEntryForm from './pages/accounting/JournalEntryForm';
import AccountLedgerIndex from './pages/accounting/AccountLedgerIndex';
import AccountLedgerDetail from './pages/accounting/AccountLedgerDetail';
import BalanceSheet from './pages/accounting/BalanceSheet';
import IncomeStatement from './pages/accounting/IncomeStatement';
import QuickEntry from './pages/accounting/QuickEntry';


function LoadingScreen() {
  return (
    <div style={{
      display:'flex', justifyContent:'center', alignItems:'center',
      height:'100vh', background:'#f5f5f5'
    }}>
      <div style={{ textAlign:'center' }}>
        <div style={{
          width:60, height:60, border:'5px solid #7C3AED',
          borderTopColor:'transparent', borderRadius:'50%',
          animation:'spin 1s linear infinite', margin:'0 auto 20px'
        }} />
        <p style={{ fontSize:18, color:'#333' }}>Chargement de l'interface admin...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const ProtectedRoute = ({ children, user, isAdmin }) => {
  if (!user || !isAdmin) return <Navigate to="/login" replace />;
  return children;
};

// Toutes les routes de l'admin panel sont à la racine (/resources,
// /courses...). Certains liens (menu, anciens favoris, liens dans des
// emails) pointent encore vers /admin/... : au lieu d'une page 404, on
// retire automatiquement le préfixe /admin et on garde le reste de l'URL
// (paramètres ?folderId=..., etc.).
function StripAdminPrefix() {
  const location = useLocation();
  const target = location.pathname.replace(/^\/admin(?=\/|$)/, '') || '/';
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      try {
        if (u) {
          const adminDoc = await getDoc(doc(db, 'admins', u.uid));
          if (adminDoc.exists()) {
            setUser(u);
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
            setUser(null);
            await auth.signOut();
          }
        } else {
          setUser(null);
          setIsAdmin(false);
        }
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <LoadingScreen />;

  const appRoutes = (
      <Routes>
        {/* Public */}
        <Route path="/login" element={user && isAdmin ? <Navigate to="/dashboard" /> : <Login />} />
        <Route path="/" element={<Navigate to={user && isAdmin ? '/dashboard' : '/login'} />} />

        {/* Anciens liens /admin/... → même page sans le préfixe */}
        <Route path="/admin/*" element={<StripAdminPrefix />} />

        {/* Dashboard */}
        <Route path="/dashboard" element={
          <ProtectedRoute user={user} isAdmin={isAdmin}><Dashboard /></ProtectedRoute>
        } />

        {/* Utilisateurs */}
        <Route path="/users" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Users /></ProtectedRoute>} />
        <Route path="/users/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><UserForm /></ProtectedRoute>} />
        <Route path="/users/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><UserForm /></ProtectedRoute>} />

        {/* Élèves */}
        <Route path="/students" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Students /></ProtectedRoute>} />
        {/* /students/new et /students/:id/edit redirigent vers UserForm :
            StudentForm.js écrivait uniquement dans la collection "students"
            sans jamais créer de document users/{uid}, ce qui rendait ces
            élèves invisibles pour tout le reste de l'app (lien
            parent-enfant, agenda, ressources...). UserForm.js fait la
            même chose correctement, avec synchronisation automatique vers
            students/teachers/parents selon le rôle. Route conservée par
            sécurité au cas où un lien externe pointerait encore ici. */}
        <Route path="/students/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><UserForm /></ProtectedRoute>} />
        <Route path="/students/:id/edit" element={<ProtectedRoute user={user} isAdmin={isAdmin}><UserForm /></ProtectedRoute>} />

        {/* Parents */}
        <Route path="/parents" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Parents /></ProtectedRoute>} />
        <Route path="/parents/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ParentForm /></ProtectedRoute>} />
        <Route path="/parents/:id/edit" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ParentForm /></ProtectedRoute>} />
        {/* ← Route ajoutée : "Gérer les liaisons" navigue vers /parents/:uid */}
        <Route path="/parents/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ParentForm /></ProtectedRoute>} />

        {/* Enseignants */}
        <Route path="/teachers" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Teachers /></ProtectedRoute>} />
        <Route path="/teachers/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><TeacherDetails /></ProtectedRoute>} />

        {/* Événements */}
        <Route path="/events" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Events /></ProtectedRoute>} />
        <Route path="/events/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><EventForm /></ProtectedRoute>} />
        <Route path="/events/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><EventDetails /></ProtectedRoute>} />
        {/* Édition : EventEdit charge l'événement existant (EventForm = création uniquement) */}
        <Route path="/events/:id/edit" element={<ProtectedRoute user={user} isAdmin={isAdmin}><EventEdit /></ProtectedRoute>} />

        {/* ── Chat ─────────────────────────────────────────────────────── */}
        <Route path="/chats" element={
          <ProtectedRoute user={user} isAdmin={isAdmin}><ChatListPage /></ProtectedRoute>
        } />
        <Route path="/chats/:chatId" element={
          <ProtectedRoute user={user} isAdmin={isAdmin}><ChatPage /></ProtectedRoute>
        } />
        <Route path="/chats/:chatId/info" element={
          <ProtectedRoute user={user} isAdmin={isAdmin}><ChatInfoPage /></ProtectedRoute>
        } />
        {/* ─────────────────────────────────────────────────────────────── */}

        {/* Annonces */}
        <Route path="/announcements" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Announcements /></ProtectedRoute>} />
        <Route path="/announcements/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><AnnouncementForm /></ProtectedRoute>} />
        <Route path="/announcements/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><AnnouncementDetails /></ProtectedRoute>} />
        <Route path="/announcements/:id/edit" element={<ProtectedRoute user={user} isAdmin={isAdmin}><AnnouncementForm /></ProtectedRoute>} />

        {/* Ressources */}
        <Route path="/resources" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Resources /></ProtectedRoute>} />
        <Route path="/resources/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ResourceForm /></ProtectedRoute>} />
        <Route path="/resources/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ResourceForm /></ProtectedRoute>} />

        {/* Dossiers de ressources (élèves spécifiques / cours / général) */}
        <Route path="/folders/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><FolderForm /></ProtectedRoute>} />
        <Route path="/folders/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><FolderForm /></ProtectedRoute>} />

        {/* Cours */}
        <Route path="/courses" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Courses /></ProtectedRoute>} />
        <Route path="/courses/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><CourseForm /></ProtectedRoute>} />
        <Route path="/courses/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><CourseDetails /></ProtectedRoute>} />
        <Route path="/courses/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><CourseEdit /></ProtectedRoute>} />
        
        {/* Styles de danse */}
<Route
    path="/dance-styles"
    element={
        <ProtectedRoute user={user} isAdmin={isAdmin}>
            <DanceStyles />
        </ProtectedRoute>
    }
/>

{/* Planning global*/}

<Route
  path="/planning"
  element={
    <ProtectedRoute user={user} isAdmin={isAdmin}>
      <Planning />
    </ProtectedRoute>
  }
/>


        {/* Catégories */}
        <Route path="/categories" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Categories /></ProtectedRoute>} />
        <Route path="/categories/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><CategoryForm /></ProtectedRoute>} />
        <Route path="/categories/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><CategoryForm /></ProtectedRoute>} />

        {/* Inscriptions */}
        <Route path="/registrations" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Registrations /></ProtectedRoute>} />

        {/* Abonnements (mensuel/semestriel + cartes 10 cours) */}
        <Route path="/subscriptions" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Subscriptions /></ProtectedRoute>} />

        {/* Paiements */}
        <Route path="/payments" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Payments /></ProtectedRoute>} />
        <Route path="/payments/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><PaymentForm /></ProtectedRoute>} />

        {/* Documents */}
        <Route path="/documents" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Documents /></ProtectedRoute>} />
        <Route path="/documents/:ownerType/:ownerId/:docId" element={<ProtectedRoute user={user} isAdmin={isAdmin}><DocumentDetails /></ProtectedRoute>} />

        {/* Objectifs */}
        <Route path="/goals" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Goals /></ProtectedRoute>} />
        <Route path="/goals/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><GoalForm /></ProtectedRoute>} />
        <Route path="/goals/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><GoalDetails /></ProtectedRoute>} />
        <Route path="/goals/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><GoalForm /></ProtectedRoute>} />

        {/* Notifications */}
        <Route path="/notifications" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Notifications /></ProtectedRoute>} />

        {/* Comptabilité */}
        <Route path="/accounting" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Accounting /></ProtectedRoute>} />
        <Route path="/accounting/chart-of-accounts" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ChartOfAccounts /></ProtectedRoute>} />
        <Route path="/accounting/journal" element={<ProtectedRoute user={user} isAdmin={isAdmin}><JournalEntries /></ProtectedRoute>} />
        <Route path="/accounting/journal/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><JournalEntryForm /></ProtectedRoute>} />
        <Route path="/accounting/journal/:id/edit" element={<ProtectedRoute user={user} isAdmin={isAdmin}><JournalEntryForm /></ProtectedRoute>} />
        <Route path="/accounting/ledger" element={<ProtectedRoute user={user} isAdmin={isAdmin}><AccountLedgerIndex /></ProtectedRoute>} />
        <Route path="/accounting/ledger/:accountId" element={<ProtectedRoute user={user} isAdmin={isAdmin}><AccountLedgerDetail /></ProtectedRoute>} />
        <Route path="/accounting/balance-sheet" element={<ProtectedRoute user={user} isAdmin={isAdmin}><BalanceSheet /></ProtectedRoute>} />
        <Route path="/accounting/income-statement" element={<ProtectedRoute user={user} isAdmin={isAdmin}><IncomeStatement /></ProtectedRoute>} />
        <Route path="/accounting/quick-entry" element={<ProtectedRoute user={user} isAdmin={isAdmin}><QuickEntry /></ProtectedRoute>} />

        {/* Présence */}
        <Route path="/attendance" element={<ProtectedRoute user={user} isAdmin={isAdmin}><AttendancePage /></ProtectedRoute>} />

        {/* Stats */}
        <Route path="/stats" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Stats /></ProtectedRoute>} />

        {/* Site web */}
        <Route path="/website" element={<ProtectedRoute user={user} isAdmin={isAdmin}><WebsiteManager /></ProtectedRoute>} />
        <Route path="/website/blog" element={<ProtectedRoute user={user} isAdmin={isAdmin}><BlogManager /></ProtectedRoute>} />
        <Route path="/website/blog/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><BlogForm /></ProtectedRoute>} />
        <Route path="/website/blog/:id/edit" element={<ProtectedRoute user={user} isAdmin={isAdmin}><BlogForm /></ProtectedRoute>} />
        <Route path="/website/classes" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ClassManager /></ProtectedRoute>} />

        {/* Règlement de l'école (contenu partagé site + app) */}
        <Route path="/school-rules" element={<ProtectedRoute user={user} isAdmin={isAdmin}><SchoolRules /></ProtectedRoute>} />

        {/* Boutique */}
        <Route path="/shop" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ProductList /></ProtectedRoute>} />
        <Route path="/shop/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ProductForm /></ProtectedRoute>} />
        <Route path="/shop/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ProductForm /></ProtectedRoute>} />
        <Route path="/shop/orders" element={<ProtectedRoute user={user} isAdmin={isAdmin}><ProductOrders /></ProtectedRoute>} />

        {/* Crew auditions — pas de protection dans l'original, conservé */}
        <Route path="/crew-auditions" element={<CrewAuditions />} />

        {/* Suivi individuel */}
        <Route path="/student-evolutions" element={<ProtectedRoute user={user} isAdmin={isAdmin}><StudentEvolutions /></ProtectedRoute>} />
        <Route path="/student-evolutions/new" element={<ProtectedRoute user={user} isAdmin={isAdmin}><StudentEvolutionForm /></ProtectedRoute>} />
        <Route path="/student-evolutions/edit/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><StudentEvolutionForm /></ProtectedRoute>} />
        <Route path="/student-evolutions/:id" element={<ProtectedRoute user={user} isAdmin={isAdmin}><StudentEvolutionDetails /></ProtectedRoute>} />
        <Route path="/student-evolutions/:profileId/evaluations/new"          element={<EvaluationForm />} />
        <Route path="/student-evolutions/:profileId/evaluations/edit/:evalId" element={<EvaluationForm />} />
        
        {/* Top pages */}
        <Route path="/top-pages" element={<ProtectedRoute user={user} isAdmin={isAdmin}><TopPages /></ProtectedRoute>} />

        {/* Contacts */}
        <Route path="/contacts" element={<ProtectedRoute user={user} isAdmin={isAdmin}><Contacts /></ProtectedRoute>} />

        {/* 🧪 Test Mollie — temporaire */}
<Route path="/test-mollie" element={<ProtectedRoute user={user} isAdmin={isAdmin}><TestMolliePayment /></ProtectedRoute>} />

        {/* 404 */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
  );

  return (
    <Router>
      {user && isAdmin ? <Layout>{appRoutes}</Layout> : appRoutes}
    </Router>
  );
}

export default App;